import { useState, useEffect } from "react";
import { Scale, Check, ArrowRight, ChevronLeft, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EXPENSE_JUSTIFICATION_URL = "https://customer-assets.emergentagent.com/job_healthcare-ops/artifacts/i2tltswd_OpsCounsel_AI_Expense_Justification.pdf";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_opscounsel-ai/artifacts/a8v8akpl_F86DF7B3-E743-4829-B779-387ACA6F68DC_L0_001%20-%20Edited.png";

const api = axios.create({ baseURL: `${BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const PricingPage = ({ onBack, onSelectPlan, user }) => {
  const [loading, setLoading] = useState(null);

  const handleSelectPlan = async (planId) => {
    if (!user) {
      // Redirect to auth
      onBack("auth");
      return;
    }
    
    setLoading(planId);
    try {
      const response = await api.post("/subscription/checkout", {
        plan_id: planId,
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (err) {
      console.error("Checkout error:", err);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => onBack()}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm">Back to OpsCounsel™</span>
          </button>
          <div className="flex items-center gap-3">
            <img 
              src={LOGO_URL} 
              alt="OpsCounsel™ AI" 
              className="h-8 w-auto object-contain"
            />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-16 pb-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 tracking-tight">
            Pricing
          </h1>
          <p className="text-xl text-primary font-medium mb-6">
            Clear thinking under pressure.
          </p>
          <p className="text-muted-foreground text-lg leading-relaxed">
            No contracts. No consulting. No meetings.
          </p>
          <p className="text-muted-foreground leading-relaxed mt-4 max-w-2xl mx-auto">
            OpsCounsel™ AI provides executive-level operational counsel for ambulatory surgery center leaders 
            who are carrying real risk and real responsibility.
          </p>
          <p className="text-foreground font-medium mt-6">
            You're paying for clarity, not hand-holding.
          </p>
        </div>
      </section>

      {/* Free Preview */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-muted/30 border border-border rounded-xl p-8 mb-12">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  Try OpsCounsel™ AI
                </h2>
                <p className="text-muted-foreground mb-4">
                  7 free messages — one time
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    Experience the depth and tone of OpsCounsel
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    See how it frames risk, priorities, and next steps
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    No credit card required
                  </li>
                </ul>
              </div>
              <div className="flex-shrink-0">
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => onBack()}
                  data-testid="start-free-btn"
                >
                  Start Free Preview
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-6 pt-4 border-t border-border/50">
              Once the preview is used, continued access requires a plan.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* Monthly */}
            <div className="bg-card border border-border rounded-xl p-6 flex flex-col">
              <h3 className="text-lg font-semibold text-foreground">
                OpsCounsel™ AI — Monthly
              </h3>
              <div className="mt-4 mb-6">
                <span className="text-4xl font-bold text-foreground">$39</span>
                <span className="text-muted-foreground"> / month</span>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                For administrators who want ongoing access to operational judgment.
              </p>
              
              <div className="space-y-3 mb-6 flex-grow">
                <p className="text-sm font-medium text-foreground">Includes:</p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    Unlimited chat access
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    All operational prompt templates
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    Governance, QAPI, staffing, finance, and survey readiness guidance
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    Cancel anytime
                  </li>
                </ul>
              </div>

              <div className="pt-4 border-t border-border/50 mb-6">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Best for:</span> Solo administrators, 
                  directors of operations, and leaders covering multiple roles.
                </p>
              </div>

              <Button 
                className="w-full" 
                variant="outline"
                onClick={() => handleSelectPlan("monthly")}
                disabled={loading === "monthly"}
                data-testid="select-monthly"
              >
                {loading === "monthly" ? "Processing..." : "Get Monthly Access"}
              </Button>
            </div>

            {/* Annual - Featured */}
            <div className="bg-card border-2 border-primary rounded-xl p-6 flex flex-col relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs px-4 py-1 rounded-full font-medium">
                Best Value
              </div>
              
              <h3 className="text-lg font-semibold text-foreground mt-2">
                OpsCounsel™ AI — Annual
              </h3>
              <div className="mt-4 mb-6">
                <span className="text-4xl font-bold text-foreground">$299</span>
                <span className="text-muted-foreground"> / year</span>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Twelve months of clarity at a reduced rate.
              </p>
              
              <div className="space-y-3 mb-6 flex-grow">
                <p className="text-sm font-medium text-foreground">Includes:</p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    Unlimited chat access
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    All prompt templates
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    Stable access through survey cycles and leadership transitions
                  </li>
                </ul>
              </div>

              <div className="pt-4 border-t border-border/50 mb-6">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Best for:</span> Leaders who want 
                  consistent decision support without thinking about monthly billing.
                </p>
              </div>

              <Button 
                className="w-full"
                onClick={() => handleSelectPlan("annual")}
                disabled={loading === "annual"}
                data-testid="select-annual"
              >
                {loading === "annual" ? "Processing..." : "Get Annual Access"}
              </Button>
            </div>

            {/* Survey Pass */}
            <div className="bg-card border border-border rounded-xl p-6 flex flex-col">
              <h3 className="text-lg font-semibold text-foreground">
                Survey Readiness Pass
              </h3>
              <div className="mt-4 mb-6">
                <span className="text-4xl font-bold text-foreground">$129</span>
                <span className="text-muted-foreground"> one-time</span>
                <p className="text-xs text-muted-foreground mt-1">60 days access</p>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Focused access during survey season.
              </p>
              
              <div className="space-y-3 mb-6 flex-grow">
                <p className="text-sm font-medium text-foreground">Includes:</p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    Unlimited chat for 60 days
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    Survey-focused prompt templates
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    Accreditation, governance, and compliance risk framing
                  </li>
                </ul>
                <p className="text-xs text-muted-foreground italic">
                  Automatically expires. No renewal.
                </p>
              </div>

              <div className="pt-4 border-t border-border/50 mb-6">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Best for:</span> Administrators 
                  preparing for AAAHC, CMS, or state surveys.
                </p>
              </div>

              <Button 
                className="w-full" 
                variant="outline"
                onClick={() => handleSelectPlan("survey_pass")}
                disabled={loading === "survey_pass"}
                data-testid="select-survey-pass"
              >
                {loading === "survey_pass" ? "Processing..." : "Get Survey Pass"}
              </Button>
            </div>
          </div>

          {/* Expense Justification */}
          <div className="mt-10 text-center">
            <p className="text-sm text-muted-foreground mb-2">Need reimbursement approval?</p>
            <a 
              href={EXPENSE_JUSTIFICATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
              data-testid="expense-justification-link"
            >
              <FileText className="w-4 h-4" />
              Download a 1-page business justification for finance or procurement
            </a>
          </div>
        </div>
      </section>

      {/* What It Is Section */}
      <section className="py-16 px-4 border-t border-border/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground mb-8 text-center">
            What OpsCounsel™ AI Is — and Is Not
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-muted/20 rounded-xl p-6">
              <h3 className="font-semibold text-foreground mb-4">OpsCounsel™ AI is:</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Executive decision support
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Operational judgment at speed
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Best-practice guidance grounded in ASC realities
                </li>
              </ul>
            </div>
            
            <div className="bg-muted/20 rounded-xl p-6">
              <h3 className="font-semibold text-foreground mb-4">OpsCounsel™ AI is not:</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5 flex-shrink-0">—</span>
                  Consulting services
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5 flex-shrink-0">—</span>
                  Legal, medical, or HR advice
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5 flex-shrink-0">—</span>
                  A guarantee of survey outcomes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5 flex-shrink-0">—</span>
                  A replacement for professional counsel
                </li>
              </ul>
            </div>
          </div>
          
          <p className="text-center text-sm text-muted-foreground mt-8">
            This boundary is intentional.
          </p>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 px-4 bg-muted/20">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-foreground mb-6">
            Why This Is Priced the Way It Is
          </h2>
          <p className="text-lg text-muted-foreground mb-6">
            One prevented misstep.<br />
            One clearer decision.<br />
            One avoided compliance gap.
          </p>
          <p className="text-foreground font-medium">
            OpsCounsel™ pays for itself quickly — without creating another obligation.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-foreground mb-6">
            Choose the Level That Fits How You Operate
          </h2>
          <div className="space-y-3 text-muted-foreground mb-8">
            <p>Want to test the value? → <span className="text-foreground">Free Preview</span></p>
            <p>Want ongoing clarity? → <span className="text-foreground">Monthly or Annual</span></p>
            <p>In survey mode right now? → <span className="text-foreground">Survey Readiness Pass</span></p>
          </div>
          <p className="text-sm text-muted-foreground">
            No upsells. No calls. No pressure.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-border/50">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-xs text-muted-foreground">
            OpsCounsel™ AI provides operational best-practice guidance and decision support only. 
            It does not provide legal, medical, or HR advice and does not replace professional counsel or regulatory guidance.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
